import { observable, computedFrom, BindingEngine, bindable, bindingMode, autoinject, containerless } from 'aurelia-framework';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { WebApiService } from "../../services/web-api-service";
import { Carrier } from '../../models/carrier';
import { AppMessageService } from "../../services/app-message-service";
import { SecurityService } from '../../services/security-service';
import { SimpleDialog } from '../dialogs/simple-dialog';
import { DialogService } from 'aurelia-dialog';
import { BusyService } from '../../services/busy-service';
import { WorkCarrier } from '../work-carrier';
@autoinject()
@containerless()
export class WorkflowActions {

    @bindable({ defaultBindingMode: bindingMode.oneWay }) carrier: Carrier;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) carrierDocuments: any;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) saveCarrierFn: () => Promise<any>;

    subscription: Subscription;

    carrierChanged(newValue, oldValue) {
        if (newValue) {
            this.renderActions(newValue);
        }
    }

    carrierDocumentsChanged(newValue, oldValue) {
        if (newValue) {
            this.renderActions(this.carrier);
        }
    }

    attached() {
        this.subscription = this.ea.subscribe("carrier.save", c => {
            if (c == this.carrier) this.renderActions(this.carrier);
        });
    }

    detached() {
        this.subscription.dispose();
    }

    constructor(
        private ea: EventAggregator,
        private dialogService: DialogService,
        private busy: BusyService,
        private api: WebApiService,
        private securityService: SecurityService,
        private appMessageService: AppMessageService
    ) { }

    actions: Array<any> = [];

    renderActions(carrier: Carrier) {
        if (carrier) {
            this.actions = this.workflowMap.filter(m => m.displayWhen());
        } else {
            this.actions = [];
        }
    }


    statusChangeReason: String = "Workflow";
    hasDocument(documentTypeCode) {
        return !!(this.carrierDocuments && this.carrierDocuments.find(d => d.documentTypeCode == documentTypeCode));
    }
    changeStatus(carrierId, statusId, comment, message) {

        return this.saveCarrierFn().then(() => {
            var params = {
                carrierId: carrierId,
                statusId: statusId,
                comment: comment
            };
            return this.api.submit("Carriers", "ChangeCarrierStatus", params)
                .then(() => this.appMessageService.addSuccessMessage(message));
        });
    }
        
    async sendMasterApprovedEmail(carrierId) {
        //const millCodeText = this.securityService.user.millCodes.join(",");
        var model = {
            title: "Send master approved email?",
            message: "This action will send an email to all internal CMP users.  Click OK to proceed.",
            showComment: false,
            comment: "",
            buttons: ["Ok", "Cancel"]
        };

        const dialogResult = await this.dialogService.open(
            {
                viewModel: SimpleDialog,
                model: model
            }).whenClosed();

        if (dialogResult.output == "Ok") {
            this.busy.on();
            //await this.workCarrierComponent.saveChanges(false);
            try {
                const message = "Master Approval Email has been sent";
                var params = {
                    id: carrierId
                };
                await this.api.submit("Carriers", "SendMasterApprovedEmail", params);
                this.carrier.isMasterApprovedEmailSent = true;
                this.renderActions(this.carrier);
                this.appMessageService.addSuccessMessage(message);

            } finally {
            this.busy.off();
            }
        }
    }

    workflowMap: any = [
        {
            text: "POTENTIAL",
            displayWhen: () => this.carrier.status == 0 && !!this.carrier.legalName && !!this.carrier.dotNumber,
            click: () => {
                var oldStatus = this.carrier.status;
                this.changeStatus(this.carrier.id, 2, this.statusChangeReason, "Carrier Moved to Potential");
            }
        },
        {
            text: "PACKET SENT",
            displayWhen: () => this.carrier.status == 2 && (this.carrier.entityType != "TRUCKS" || !!this.carrier.contractType),
            click: () => {
                var oldStatus = this.carrier.status;
                this.changeStatus(this.carrier.id, 3, this.statusChangeReason, "Carrier Moved to In Process");
            }
        },
        {
            text: "IN PROCESS",
            displayWhen: () => {
                if (this.carrier.entityType != 'TRUCKS') {
                    return this.carrier.status == 3 && this.hasDocument("CT") && this.hasDocument("FS");
                } else {
                    return this.carrier.status == 3 && this.hasDocument("CT") && !!this.carrier.scacCode;
                }
            },
            click: () => {
                var oldStatus = this.carrier.status;
                this.changeStatus(this.carrier.id, 4, this.statusChangeReason, "Carrier Moved to In Process");
            }
        },
        {
            text: "INSURANCE REVIEW",
            displayWhen: () => {
                if (this.carrier.entityType != 'TRUCKS') {
                    return this.carrier.status == 4 && this.hasDocument("CT");
                } else {
                    return this.carrier.status == 4 && this.hasDocument("CT") && !!this.carrier.scacCode;
                }
            },
            click: () => {
                var oldStatus = this.carrier.status;
                this.changeStatus(this.carrier.id, 5, this.statusChangeReason, "Carrier Moved to Insurance Review");
            }
        },
        {
            text: "INSURANCE COMPLETE",
            displayWhen: () => this.carrier.status == 5,
            click: () => {
                var oldStatus = this.carrier.status;
                this.changeStatus(this.carrier.id, 6, this.statusChangeReason, "Carrier Moved to Insurance Complete");
            }
        },
        {
            text: "NLC APPROVED",
            displayWhen: () => {
                if (this.securityService.hasRole("Legal")) {
                    if (this.carrier.status == 6) {
                        if (this.carrier.entityType == 'TRUCKS') {
                            return !!this.carrier.dotNumber && !!this.carrier.scacCode;
                        } else {
                            return !!this.carrier.taxId && !!this.carrier.scacCode;
                        }
                    }
                }
                return false;
            },
            click: () => {
                var oldStatus = this.carrier.status;
                this.changeStatus(this.carrier.id, 7, this.statusChangeReason, "Carrier Moved to NLC Approved");
            }
        },
        {
            text: "MASTER APPROVED",
            displayWhen: () => this.securityService.hasRole("Legal") && this.carrier.status == 7,
            click: () => {
                var oldStatus = this.carrier.status;
                this.changeStatus(this.carrier.id, 8, this.statusChangeReason, "Carrier Moved to Master Approved");
            }
        },
        {
            text: "SEND MASTER APPROVED EMAIL NOTICE",
            displayWhen: () => this.securityService.hasRole("Legal") && this.carrier.status == 8 && !this.carrier.isMasterApprovedEmailSent,
            click: async () => {
                await this.sendMasterApprovedEmail(this.carrier.id);
            }
        }
    ];


}